/**
 * Shared helpers for grouping T&M / RFI tickets by project and building
 * compact status summaries for Projects Home cards (and Project Page).
 */

import { RFI_STATUS_LABELS } from "../../operations/rfi/constants";
import { getLatestInspector } from "../../operations/tmlog/helpers";

/**
 * Return whether a ticket belongs to a project (by job PK or job number).
 *
 * @param {Object} ticket - T&M/RFI API row.
 * @param {Object} project - Project / JobNumber API row.
 * @returns {boolean}
 */
export function isTicketForProject(ticket, project) {
  const projectId = String(project.id);
  const projectNumber = String(project.number || "");
  const ticketJobId = ticket.job == null ? "" : String(ticket.job);
  const ticketJobNumber =
    ticket.job_number == null ? "" : String(ticket.job_number);

  return (
    ticketJobId === projectId ||
    Boolean(projectNumber && ticketJobNumber === projectNumber)
  );
}

/**
 * Build a map of project id → tickets that belong to that project.
 *
 * @param {Array<Object>} projects - Project API rows.
 * @param {Array<Object>} tickets - T&M or RFI API rows.
 * @returns {Map<string, Array<Object>>}
 */
export function groupTicketsByProjectId(projects, tickets) {
  const byProjectId = new Map();
  const projectByJobId = new Map();
  const projectByNumber = new Map();

  for (const project of projects || []) {
    const key = String(project.id);
    byProjectId.set(key, []);
    projectByJobId.set(key, project);
    if (project.number != null && project.number !== "") {
      projectByNumber.set(String(project.number), project);
    }
  }

  for (const ticket of tickets || []) {
    let project = null;
    if (ticket.job != null && ticket.job !== "") {
      project = projectByJobId.get(String(ticket.job)) || null;
    }
    if (!project && ticket.job_number != null && ticket.job_number !== "") {
      project = projectByNumber.get(String(ticket.job_number)) || null;
    }
    if (!project) {
      continue;
    }
    byProjectId.get(String(project.id)).push(ticket);
  }

  return byProjectId;
}

/**
 * Compact T&M badge counts for a project card (mutually exclusive).
 *
 * - Created (open): not sent to an inspector yet
 * - Sent: sent, but not signed
 * - Signed: signed, but not submitted/closed
 * Submitted/priced reports are ignored (no dollar amounts on badges).
 *
 * @param {Array<Object>} reports - Project-scoped T&M rows.
 * @returns {{ open: number, sent: number, signed: number }}
 */
export function buildTmCardSummary(reports) {
  const rows = reports || [];
  let open = 0;
  let sent = 0;
  let signed = 0;

  for (const report of rows) {
    // Submitted/closed tags are out of badge scope.
    if (report.submitted) {
      continue;
    }
    const latestInspector = getLatestInspector(report.inspectors);
    if (latestInspector?.signed_at) {
      signed += 1;
    } else if (latestInspector) {
      sent += 1;
    } else {
      open += 1;
    }
  }

  return { open, sent, signed };
}

/**
 * Compact RFI status counts for a project card (mutually exclusive statuses).
 *
 * @param {Array<Object>} rfis - Project-scoped RFI rows.
 * @returns {{ open: number, reviewing: number, responding: number, closed: number }}
 */
export function buildRfiCardSummary(rfis) {
  const counts = {
    open: 0,
    reviewing: 0,
    responding: 0,
    closed: 0,
  };

  for (const rfi of rfis || []) {
    const status = rfi.status || "new";
    if (status === "closed") {
      counts.closed += 1;
    } else if (status === "awaiting_response") {
      counts.responding += 1;
    } else if (status === "open") {
      counts.reviewing += 1;
    } else {
      // ``new`` and any unknown map to Open (matches RFI log label for new).
      counts.open += 1;
    }
  }

  return counts;
}

/** Display labels for RFI card rows (Responding = awaiting_response). */
export const PROJECT_CARD_RFI_STATUS_LABELS = {
  open: RFI_STATUS_LABELS.new,
  reviewing: RFI_STATUS_LABELS.open,
  responding: "Responding",
  closed: RFI_STATUS_LABELS.closed,
};

/**
 * Empty T&M card summary used while tickets load or when a project has none.
 *
 * @returns {{ open: number, sent: number, signed: number }}
 */
export function emptyTmCardSummary() {
  return { open: 0, sent: 0, signed: 0 };
}

/**
 * Empty RFI card summary used while tickets load or when a project has none.
 *
 * @returns {{ open: number, reviewing: number, responding: number, closed: number }}
 */
export function emptyRfiCardSummary() {
  return { open: 0, reviewing: 0, responding: 0, closed: 0 };
}
